import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, Button, Dropdown } from '@lucid/lego';
import styles from './Details.module.scss';
import type { UserManagementUser, UserLocation } from '../utils';
import { toUserLocation } from '../utils';
import { useStore } from '../../../../store/store';
import { escapeHtml } from '../../../../utils/tools';
import { useUpdateUserLocations } from '../../../../api/services/lupp/locations/locations';
import { StatusMessage } from '../../../OrderHub/ManageOrders/Details/Registration/StatusMessage/StatusMessage';

/** How long the save status banner stays visible before auto-dismissing. */
const STATUS_MESSAGE_DURATION_MS = 3000;

/** idpRoleName values from the partners API that gate location-editing behavior. */
const ADMIN_ROLE = 'LucidPartners-Agency-Admin';
const MANAGER_ROLE = 'LucidPartners-Agency-Manager';

type DetailsProps = {
  selectedUser: UserManagementUser | null;
  availableLocations: UserLocation[];
  organizationIdentifier: string;
  onLocationUpdate: (userId: string, newLocations: UserLocation[]) => void;
};

/** Renders a user's assigned locations, or a placeholder when none are assigned. */
const AssignedLocations: React.FC<{ locations: UserLocation[] }> = ({ locations }) => {
  if (locations.length === 0) {
    return (
      <Typography color="gray-5" size="small" variant="body">
        No locations assigned.
      </Typography>
    );
  }

  return (
    <>
      {locations.map((location) => (
        <Box key={location.id} mb={8}>
          <Typography color="black" size="medium" variant="body">
            {escapeHtml(location.name)}
            {location.isPrimary ? ' (Primary)' : ''}
          </Typography>
          <Typography color="gray-7" size="small" variant="body">
            {escapeHtml(location.address)}
          </Typography>
        </Box>
      ))}
    </>
  );
};

/**
 * Details component for displaying user information.
 * Shows user details with editable location field.
 *
 * @component
 * @param {DetailsProps} props - Component props
 * @returns {JSX.Element} User details view
 */
const Details: React.FC<DetailsProps> = ({
  selectedUser,
  availableLocations,
  organizationIdentifier,
  onLocationUpdate,
}) => {
  const theme = useStore((state) => state.theme);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedLocationIds, setSelectedLocationIds] = useState<string[]>([]);
  const [saveStatus, setSaveStatus] = useState<'success' | 'error' | null>(null);
  const statusTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { mutate: updateUserLocations, isPending: isSaving } = useUpdateUserLocations();

  useEffect(() => {
    return () => {
      if (statusTimeoutRef.current) {
        clearTimeout(statusTimeoutRef.current);
      }
    };
  }, []);

  // Reset local edit state when navigating to a different user so stale edits/messages don't linger.
  useEffect(() => {
    if (statusTimeoutRef.current) {
      clearTimeout(statusTimeoutRef.current);
    }
    setIsEditing(false);
    setSelectedLocationIds([]);
    setSaveStatus(null);
  }, [selectedUser?.id]);

  const showSaveStatus = (status: 'success' | 'error') => {
    if (statusTimeoutRef.current) {
      clearTimeout(statusTimeoutRef.current);
    }
    setSaveStatus(status);
    statusTimeoutRef.current = setTimeout(() => {
      setSaveStatus(null);
    }, STATUS_MESSAGE_DURATION_MS);
  };

  if (!selectedUser) {
    return (
      <Box pt={32} pr={32} pb={24} pl={32}>
        <Typography color="white" variant="subtitle" size="medium" theme={theme} align="center">
          There is no user to view details.
        </Typography>
      </Box>
    );
  }

  const isTargetAdmin = selectedUser.role === ADMIN_ROLE;
  const isTargetManager = selectedUser.role === MANAGER_ROLE;

  const handleEditClick = () => {
    // Non-managers may only be assigned a single location; seed with the primary (or first) one.
    const initialLocationIds = isTargetManager
      ? selectedUser.locations.map((location) => location.id)
      : [
          (
            selectedUser.locations.find((location) => location.isPrimary) ??
            selectedUser.locations[0]
          )?.id,
        ].filter((id): id is string => Boolean(id));
    setSelectedLocationIds(initialLocationIds);
    if (statusTimeoutRef.current) {
      clearTimeout(statusTimeoutRef.current);
    }
    setSaveStatus(null);
    setIsEditing(true);
  };

  const handleCancelClick = () => {
    setIsEditing(false);
    setSelectedLocationIds([]);
  };

  const handleSaveClick = () => {
    const userId = selectedUser.id;
    updateUserLocations(
      { organizationIdentifier, userId, locationIdentifiers: selectedLocationIds },
      {
        onSuccess: (updatedLocations) => {
          onLocationUpdate(
            userId,
            updatedLocations.map((location) => toUserLocation(location))
          );
          setIsEditing(false);
          showSaveStatus('success');
        },
        onError: () => {
          showSaveStatus('error');
        },
      }
    );
  };

  return (
    <Box pl={16} pb={24} className={styles.scrollable}>
      <Box className={styles.container}>
        {/* User Information Card */}
        <Box
          pb={24}
          pl={24}
          pr={24}
          pt={16}
          className={styles.cardBox}
          background={'white'}
          theme={theme}
        >
          <Typography color="black" variant="subtitle" size="medium" theme={theme}>
            User Information
          </Typography>
          {saveStatus === 'success' && (
            <Box pt={16}>
              <StatusMessage message="Changes saved successfully" type="success" theme={theme} />
            </Box>
          )}
          {saveStatus === 'error' && (
            <Box pt={16}>
              <StatusMessage
                message="Failed to save changes. Please try again."
                type="error"
                theme={theme}
              />
            </Box>
          )}
          <Box
            background="gray-1"
            pb={16}
            pl={24}
            pr={24}
            pt={16}
            mt={16}
            className={styles.cardBox}
          >
            <Box mb={12}>
              <Typography theme="light" color="primary-1" size="medium" variant="label">
                User Name
              </Typography>
              <Typography color="black" size="medium" variant="body">
                {escapeHtml(selectedUser.name)}
              </Typography>
            </Box>
            <Box mb={12}>
              <Typography theme="light" color="primary-1" size="medium" variant="label">
                Email
              </Typography>
              <Typography color="black" size="medium" variant="body">
                {escapeHtml(selectedUser.email)}
              </Typography>
            </Box>
            <Box mb={12}>
              <Typography theme="light" color="primary-1" size="medium" variant="label">
                Role
              </Typography>
              <Typography color="black" size="medium" variant="body">
                {escapeHtml(selectedUser.role) || '-'}
              </Typography>
            </Box>
            <Box>
              <Box className={styles.locationHeader}>
                <Typography theme="light" color="primary-1" size="medium" variant="label">
                  Locations
                </Typography>
                {!isEditing && !isTargetAdmin && (
                  <Button
                    theme="light"
                    color="neutral"
                    size="small"
                    type="tertiary"
                    onClick={handleEditClick}
                    gaComponent="user-management"
                    dataTestId="edit-location-button"
                    label="Edit"
                    iconLeft={{ category: 'global', name: 'edit' }}
                  />
                )}
              </Box>
              {!isEditing ? (
                <AssignedLocations locations={selectedUser.locations} />
              ) : (
                <>
                  <Box mt={8}>
                    {isTargetManager ? (
                      <Dropdown
                        theme="light"
                        label=""
                        name="location"
                        multiSelect
                        selectedOptions={selectedLocationIds}
                        options={availableLocations.map((location) => ({
                          value: location.id,
                          label: `${location.name} - ${location.address}`,
                        }))}
                        handleMultiSelect={(values: string[]) => setSelectedLocationIds(values)}
                      />
                    ) : (
                      <Dropdown
                        theme="light"
                        label=""
                        name="location"
                        selectedOption={selectedLocationIds[0]}
                        options={availableLocations.map((location) => ({
                          value: location.id,
                          label: `${location.name} - ${location.address}`,
                        }))}
                        handleSelect={(value: string) => setSelectedLocationIds([value])}
                      />
                    )}
                  </Box>
                  <Box className={styles.buttonGroup}>
                    <Button
                      theme="light"
                      color="neutral"
                      type="secondary"
                      size="small"
                      onClick={handleCancelClick}
                      disabled={isSaving}
                      gaComponent="user-management"
                      dataTestId="cancel-location-button"
                      label="Cancel"
                    />
                    <Button
                      theme="light"
                      color="neutral"
                      type="primary"
                      size="small"
                      onClick={handleSaveClick}
                      disabled={isSaving}
                      gaComponent="user-management"
                      dataTestId="save-location-button"
                      label={isSaving ? 'Saving...' : 'Save'}
                    />
                  </Box>
                </>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Details;
